import { Injectable, signal } from '@angular/core';

/**
 * Phase 25 / ADHD Phase 1 — Haptic anchor (60-minute pulse).
 *
 * Externalizes time. ADHD time-blindness is partly the body losing track of
 * how long things take; an unobtrusive hourly pulse — a short vibration on
 * mobile and a soft transient banner on desktop — gives the body a felt
 * marker without inserting a "you're behind on X" message. Aligned to the
 * top of the local hour so the rhythm matches the world the user lives in
 * (3:00, 4:00, etc.) instead of "60 min from app open."
 *
 * Limitations accepted for v1: only fires while the SPA is open in a tab.
 * Server-side push (PushSubscription + a daily/hourly hosted job) would let
 * us fire when the app is closed, but that's a bigger surface; the kept-
 * promise of v1 is "while you're using this, you'll feel the hour pass."
 *
 * Off by default. Persisted to localStorage so the choice survives reloads.
 */
@Injectable({ providedIn: 'root' })
export class HapticAnchorService {
  private static readonly StorageKey = 'cc.haptic-anchor.on';

  /** True when the user has opted in. Drives the settings toggle UI. */
  readonly enabled = signal(HapticAnchorService.readStored());

  /** True for ~3 seconds after a pulse fires; the shell renders a soft chime when set. */
  readonly pulseActive = signal(false);

  private alignTimer?: ReturnType<typeof setTimeout>;
  private hourlyTimer?: ReturnType<typeof setInterval>;
  private clearPulseTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    if (this.enabled()) this.startSchedule();
  }

  /** Toggle on. No-op if already on. Persisted to localStorage. */
  enable(): void {
    if (this.enabled()) return;
    this.enabled.set(true);
    HapticAnchorService.writeStored(true);
    this.startSchedule();
  }

  /** Toggle off. Cancels the schedule and clears any in-progress pulse. */
  disable(): void {
    if (!this.enabled()) return;
    this.enabled.set(false);
    HapticAnchorService.writeStored(false);
    this.stopSchedule();
    this.pulseActive.set(false);
  }

  /**
   * Fire a one-time test pulse so the user can confirm the haptic feels
   * right (some devices have vibration disabled at the OS level). Doesn't
   * affect the schedule.
   */
  triggerNow(): void {
    this.fire();
  }

  // ----- internals ----------------------------------------------------------

  /**
   * Schedule the first pulse for the next top-of-hour, then a recurring
   * 60-min interval after that. setTimeout drift is acceptable at this
   * cadence — a few seconds either side of 4:00 is fine, the point is the
   * felt rhythm, not stopwatch precision.
   */
  private startSchedule(): void {
    this.stopSchedule();
    const now = new Date();
    const msIntoHour = (now.getMinutes() * 60 + now.getSeconds()) * 1000 + now.getMilliseconds();
    const msUntilTop = (60 * 60 * 1000) - msIntoHour;

    this.alignTimer = setTimeout(() => {
      this.fire();
      this.hourlyTimer = setInterval(() => this.fire(), 60 * 60 * 1000);
    }, msUntilTop);
  }

  private stopSchedule(): void {
    if (this.alignTimer) { clearTimeout(this.alignTimer); this.alignTimer = undefined; }
    if (this.hourlyTimer) { clearInterval(this.hourlyTimer); this.hourlyTimer = undefined; }
    if (this.clearPulseTimer) { clearTimeout(this.clearPulseTimer); this.clearPulseTimer = undefined; }
  }

  /**
   * Fire one pulse. Vibration is silent on devices that support it; the
   * pulseActive signal lets the shell render a fading visual chime for
   * desktops where vibration is unavailable. We don't request notification
   * permission here — that's the user's separate web-push opt-in.
   */
  private fire(): void {
    try {
      // 90ms / 60ms gap / 90ms — short enough to feel like a heartbeat,
      // long enough to register on most haptic motors.
      const nav = globalThis.navigator as Navigator & { vibrate?: (pattern: number | number[]) => boolean };
      nav.vibrate?.([90, 60, 90]);
    } catch { /* vibrate not supported — silent fallback to the visual chime */ }

    this.pulseActive.set(true);
    if (this.clearPulseTimer) clearTimeout(this.clearPulseTimer);
    this.clearPulseTimer = setTimeout(() => this.pulseActive.set(false), 3000);
  }

  private static readStored(): boolean {
    try { return globalThis.localStorage?.getItem(HapticAnchorService.StorageKey) === '1'; }
    catch { return false; }
  }

  private static writeStored(v: boolean): void {
    try { globalThis.localStorage?.setItem(HapticAnchorService.StorageKey, v ? '1' : '0'); }
    catch { /* localStorage unavailable — in-memory only */ }
  }
}
